import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Button, Input, Card, Spinner } from '@/components/ui'
import { api, ApiError } from '@/services/api'
import { getErrorMessage } from '@/services/errors'
import { ErrorCode } from '@/services/types'

interface ResetPasswordFormData {
  newPassword: string
  confirmPassword: string
}

type PageState = 'loading' | 'valid' | 'invalid' | 'success'

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [pageState, setPageState] = useState<PageState>('loading')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>()

  const newPassword = watch('newPassword')

  useEffect(() => {
    if (!token) {
      setPageState('invalid')
      return
    }

    const verifyToken = async () => {
      try {
        const response = await api.verifyPasswordResetToken(token)
        setPageState(response.valid ? 'valid' : 'invalid')
      } catch {
        setPageState('invalid')
      }
    }

    verifyToken()
  }, [token])

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return

    setIsSubmitting(true)
    setError(null)

    try {
      await api.completePasswordReset(token, data.newPassword)
      setPageState('success')
    } catch (err) {
      if (err instanceof ApiError) {
        switch (err.code) {
          case ErrorCode.PASSWORD_RESET_TOKEN_EXPIRED:
            setError('This reset link has expired. Please request a new one.')
            break
          case ErrorCode.PASSWORD_RESET_TOKEN_INVALID:
          case ErrorCode.PASSWORD_RESET_TOKEN_USED:
            setError('This reset link is no longer valid. Please request a new one.')
            break
          case ErrorCode.VALIDATION_ERROR:
            setError(
              (err.details?.message as string) ||
                "Password doesn't meet requirements."
            )
            break
          default:
            setError(getErrorMessage(err, 'Failed to reset password. Please try again.'))
        }
      } else {
        setError('Failed to reset password. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderContent = () => {
    switch (pageState) {
      case 'loading':
        return (
          <div className="flex flex-col items-center py-8">
            <Spinner size="lg" />
            <p className="text-secondary-text mt-4">Verifying reset link...</p>
          </div>
        )

      case 'invalid':
        return (
          <div className="text-center py-4">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-error"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-medium mb-2">Invalid or expired link</h2>
            <p className="text-secondary-text text-sm mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link to="/auth/forgot-password">
              <Button>Request New Link</Button>
            </Link>
          </div>
        )

      case 'success':
        return (
          <div className="text-center py-4">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-lg font-medium mb-2">Password reset successful</h2>
            <p className="text-secondary-text text-sm mb-6">
              Your password has been reset. You can now log in with your new password.
            </p>
            <Button onClick={() => navigate('/login')}>Go to Login</Button>
          </div>
        )

      case 'valid':
        return (
          <>
            <p className="text-secondary-text text-sm mb-6">
              Enter your new password below.
            </p>

            {error && (
              <div className="mb-4 p-3 border border-error bg-red-50">
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Input
                label="New Password"
                type="password"
                {...register('newPassword', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                    message:
                      'Password must include uppercase, lowercase, and a number',
                  },
                })}
                error={errors.newPassword?.message}
              />

              <Input
                label="Confirm Password"
                type="password"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) =>
                    value === newPassword || 'Passwords do not match',
                })}
                error={errors.confirmPassword?.message}
              />

              <div className="text-xs text-secondary-text">
                Password must be at least 8 characters with uppercase, lowercase, and a number.
              </div>

              <Button type="submit" loading={isSubmitting} className="w-full">
                Reset Password
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-sm text-secondary-text hover:text-primary-text"
              >
                Back to login
              </Link>
            </div>
          </>
        )
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-bg px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-2xl font-medium">
            Beacon
          </Link>
          <p className="text-secondary-text mt-2">Reset your password</p>
        </div>

        <Card>{renderContent()}</Card>
      </div>
    </div>
  )
}
